import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import globalStyle from "../../globalStyle";
import GlobalButton from "../CustomButton";
import Label from "../Label";
import TextInput from "../TextInputs";
import { changePasswordValidation } from "../../utils/Validation";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
  "& .MuiDialogActions-root": {
    padding: "25px 16px",
    [theme.breakpoints.down("sm")]: {
      padding: "25px  8px",
    },
  },
}));

let paramsData = {};

const ChangePasswordDialog = forwardRef(({ handleChangePassword }, ref) => {
  const [open, setOpen] = useState(false);

  const [inputValues, setInputValues] = useState({
    newPassword: "",
    reEnterPassword: "",
    oldPassword: "",
  });

  const [inputError, setInputError] = useState({
    newPassword: false,
    isPasswordStrong: false,
    reEnterPassword: false,
    isPasswordMatch: false,
    oldPassword: false,
  });

  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    reEnterPassword: false,
    oldPassword: false,
  });

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      paramsData = params;
      setOpen(true);
    },
    closeDialog(params) {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues({});
    setInputError({});
    setShowPassword({
      newPassword: false,
      reEnterPassword: false,
      oldPassword: false,
    });
  };

  const handleChangeInputValues = (event) => {
    const { name, value } = event.target;
    setInputError((prevErr) => ({
      ...prevErr,
      [name]: false,
      isPasswordStrong:
        name === "newPassword" ? false : inputError.isPasswordStrong,
      isPasswordMatch:
        name === "reEnterPassword" ? false : inputError.isPasswordMatch,
    }));
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const toggleShowPassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <IconButton
        sx={{ position: "absolute", top: "5px", right: "5px" }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle
        variant="h1"
        fontWeight={700}
        fontSize={"1.5rem"}
        fontFamily={"inter"}
        sx={{ m: 0, p: 2 }}
        id="customized-dialog-title"
      >
        {t("changePassword")}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Label
              labelText={t("oldPassword")}
              required
              htmlFor={"old-password"}
            />
            <TextInput
              styleProps={globalStyle.textField}
              id={"old-password"}
              required
              inputError={inputError.oldPassword}
              helperText={inputError.oldPassword && t("oldPasswordRequired")}
              type={!showPassword.oldPassword ? "text" : "password"}
              placeholder={`${t("enter")} ${t("oldPassword")}`}
              name={"oldPassword"}
              value={inputValues.oldPassword}
              onChange={handleChangeInputValues}
              InputEndIcon={
                // Pass the InputEndIcon prop
                showPassword.oldPassword ? (
                  <VisibilityOff
                    sx={{ cursor: "pointer" }}
                    onClick={() => toggleShowPassword("oldPassword")}
                  />
                ) : (
                  <Visibility
                    sx={{ cursor: "pointer" }}
                    onClick={() => toggleShowPassword("oldPassword")}
                  />
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Label
              labelText={t("newPassword")}
              required
              htmlFor={"new-password"}
            />
            <TextInput
              styleProps={globalStyle.textField}
              id={"new-password"}
              required
              inputError={inputError.newPassword || inputError.isPasswordStrong}
              helperText={
                inputError.newPassword
                  ? t("newPasswordRequired")
                  : inputError.isPasswordStrong
                  ? t("passwordRequirements")
                  : null
              }
              type={!showPassword.newPassword ? "text" : "password"}
              placeholder={`${t("enter")} ${t("newPassword")}`}
              name={"newPassword"}
              value={inputValues.newPassword}
              onChange={handleChangeInputValues}
              InputEndIcon={
                // Pass the InputEndIcon prop
                showPassword.newPassword ? (
                  <VisibilityOff
                    sx={{ cursor: "pointer" }}
                    onClick={() => toggleShowPassword("newPassword")}
                  />
                ) : (
                  <Visibility
                    sx={{ cursor: "pointer" }}
                    onClick={() => toggleShowPassword("newPassword")}
                  />
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Label
              labelText={`${t("reEnter")} ${t("password")}`}
              required
              htmlFor={"re-enter-password"}
            />
            <TextInput
              styleProps={globalStyle.textField}
              id={"re-enter-password"}
              required
              inputError={
                inputError.reEnterPassword || inputError.isPasswordMatch
              }
              helperText={
                inputError.reEnterPassword
                  ? `${t("reEnter")} ${t("password")}`
                  : inputError.isPasswordMatch
                  ? t("passwordMismatch")
                  : null
              }
              type={"password"}
              placeholder={`${t("reEnter")} ${t("password")}`}
              name={"reEnterPassword"}
              value={inputValues.reEnterPassword}
              onChange={handleChangeInputValues}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <GlobalButton
          variant={"outlined"}
          btnText={"cancel"}
          styleProps={{ height: 40 }}
          handleClickBtn={handleClose}
        />
        <GlobalButton
          variant={"contained"}
          btnText={"yes"}
          styleProps={{ height: 40 }}
          handleClickBtn={() => {
            const validate = changePasswordValidation(
              inputValues,
              setInputError
            );
            if (validate) {
              return;
            } else {
              handleChangePassword(inputValues);
            }
          }}
        />
      </DialogActions>
    </BootstrapDialog>
  );
});

export default ChangePasswordDialog;
